import { RoundedFilledButton } from '@/components/buttons/Buttons';
import Dialog from '@/components/dialog/Dialog';
import LoadingBlocker from '@/components/dialog/LoadingBlocker';
import { TextHeadlineSmall } from '@/components/text/Texts';

import { showNotification, showError } from '@/modules/notification/engine/notification.library';
import { useState } from 'react';
import PaymentMethodForm, { DIALOG_STATE, usePaymentMethodForm } from '../components/forms/PaymentMethodForm';
import DeclinedByBankTemplate from '../components/templates/DeclinedByBankTemplate';
import styles from './billing-dialog.module.css';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import ConfirmModal from '@/modules/people/components/ConfirmModal';

import { ensureXendit, createMultiUseCardToken } from '@/services/xendit.service';
import { BillingApi } from '../billing.api';

type Props = {
	cardId: number;
	onCardUpdated: () => void;
};

const RequestEditPaymentFormDialog = (props: Props) => {
	const modal = useModal();
	const [formBlocked, setFormBlocked] = useState(false);

	const [dialogState, setDialogState] = useState<keyof typeof DIALOG_STATE>(DIALOG_STATE.ACTIVE
	);

	// Use custom hook to manage payment form
	const paymentForm = usePaymentMethodForm();

	const handleCancel = async () => {
		const closeDialog = () => {
			modal.remove();
			setFormBlocked(false);
			setDialogState(DIALOG_STATE.ACTIVE);
			paymentForm.reset(); // Reset form when closing
		};

		await NiceModal.show(ConfirmModal, {
			title: 'Discard Changes?',
			message: 'You have unsaved changes. Are you sure you want to discard your changes?',
			acceptLabel: 'Discard Changes',
			rejectLabel: 'Cancel',
			onAccept: async () => closeDialog(),
		})
	}

	const handleSubmit = async () => {
		// Trigger validation
		const isValid = await paymentForm.trigger();

		if (!isValid) {
			return;
		}

		// Get form values
		const formData = paymentForm.getValues();
		const [expMonth, expYearShort] = formData.ExpDate.split('/');
		const expYear = `20${expYearShort.trim()}`;

		setFormBlocked(true);
		setDialogState(DIALOG_STATE.ACTIVE);

		try {
			const { PublicKey, XenPlatformAccountId, VerificationAmount } = await BillingApi.getXenditConfig();
			await ensureXendit(PublicKey);

			const token = await createMultiUseCardToken({
				cardNumber: formData.CardNumber.replace(/\s/g, ''),
				expMonth: expMonth.trim(),
				expYear,
				cvn: formData.SecurityCode,
				amount: VerificationAmount,
				onBehalfOf: XenPlatformAccountId,
			});

			const res = await BillingApi.createPaymentMethodChangeRequest({
				OldCardId: props.cardId,
				CardDetails: {
					TokenId: token.id,
					Brand: token.card_info?.brand,
					MaskedCardNumber: token.masked_card_number,
					ExpMonth: parseInt(expMonth, 10),
					ExpYear: parseInt(expYear, 10),
				},
			});

			showNotification(
				res.Data?.Status === 'Approved'
					? 'Payment method updated successfully.'
					: 'Update request submitted. Approve it under the Requests tab to activate it.'
			);
			props.onCardUpdated();
			modal.remove();
		} catch (e: any) {
			if (e?.message && !e?.response) {
				showError(e.message);
			}
			setDialogState(DIALOG_STATE.ERROR);
		} finally {
			setFormBlocked(false);
		}
	};

	const handleDismiss = () => {
		setFormBlocked(false);
		setDialogState(DIALOG_STATE.ACTIVE);
		modal.remove();
		showNotification(
			'This payment method is currently not supported by the selected service, please try another payment method.',
		);
		showNotification('Update payment account request sent.');
	};

	const handleProceed = () => {
		setFormBlocked(false);
		setDialogState(DIALOG_STATE.ACTIVE);
	};

	return (
		<Dialog
			className={styles.dialog}
			visible={true}
			onHide={() => modal.remove()}
			closable={false}
		>
			<div className="px-2">
				<TextHeadlineSmall className="text-onSurface">
					Request edit payment account
				</TextHeadlineSmall>

				<p className="mt-4 tracking-[0.25px]">
					To proceed with this request, please provide the necessary
					payment account details.
				</p>
			</div>

			<hr className="mt-6 border-neutralVariant30" />

			{dialogState === DIALOG_STATE.ACTIVE && (
				<LoadingBlocker
					blocked={formBlocked}
					pt={{
						mask: {
							className: styles.loadingBlocker,
						},
					}}
					blockTemplate="Adding Card"
					color="#0A84FF"
					thin
					full
				>
					<PaymentMethodForm
						className="px-2 py-6"
						control={paymentForm.control}
					/>
				</LoadingBlocker>
			)}

			{dialogState === DIALOG_STATE.ERROR && (
				<div className="p-6">
					<DeclinedByBankTemplate
						onDismiss={handleDismiss}
						onProceed={handleProceed}
					/>
				</div>
			)}

			<div className={styles.footer}>
				<RoundedFilledButton
					label="Cancel"
					id="cancel-request-button"
					thin
					clear
					onClick={handleCancel}
				/>
				<RoundedFilledButton
					label="Request"
					id="request-button"
					thin
					disabled={!paymentForm.formState.isValid}
					onClick={handleSubmit}
				/>
			</div>
		</Dialog>
	);
};

export default RequestEditPaymentFormDialog;
